import Events from './Events'
import Field from './Field'


const defaultOptions = {
  initialValues: {},
}

export type FormOpts = {
  name?: string
  fields: object
  initialValues?: object
}

class Form {

  private _events: Events
  name: string
  opts: FormOpts
  fields: any
  isChanged: boolean
  isValid: boolean

  constructor(opts: FormOpts) {
    this._events = new Events()

    // protect from passing initialValues: null
    if (!opts.initialValues) {
      opts.initialValues = {}
    }

    this.name       = opts.name
    this.opts       = { ...defaultOptions, ...opts }
    this.fields     = {}
    this.isChanged  = false // TODO connect to Field
    this.isValid    = true

    this.setupFields()
  }

  setupFields() {
    Object.keys(this.opts.fields).forEach((fieldName) => {
      const initialValue  = this.opts.initialValues[fieldName]
      let fieldOpts       = this.opts.fields[fieldName]

      fieldOpts = Array.isArray(fieldOpts) ? { validate: fieldOpts } : fieldOpts

      // TODO if we should move this into Field
      if (typeof initialValue !== 'undefined') {
        fieldOpts.initialValue = initialValue
        fieldOpts.value = initialValue
      }

      const field = new Field(fieldName, fieldOpts, this)

      this.fields[fieldName] = field

      field.on('change', () => {
        this._events.dispatch('change', field)
      })

      field.on('blur', () => {
        this._events.dispatch('blur', field)
      })
    })
  }

  /**
   * Replace all fields "initialValue" value.
   * It means that if any field value will not passed in "values" then this field "initialValue" be reset.
   */
  setInitialValues(values: object) {
    this.opts.initialValues = values

    // TODO should we be able to update initialValues w/o changing current values? (for example for resetting to these values)
    Object.keys(this.fields).forEach((fieldName) => {
      const field = this.fields[fieldName]
      const value = values[fieldName]

      field.setInitialValue(value)
    })
  }

  /**
   * Update fields with passed values
   */
  setValues(values: object) {
    // TODO should we mark form as changed and validate it?
    Object.keys(values).forEach((fieldName) => {
      const value = values[fieldName]
      const field = this.fields[fieldName]

      if (field) {
        field.set(value === undefined || value === null ? '' : value)
      }
    })
  }

  getValues(): { [key: string]: string | boolean } {
    const values = {}

    Object.keys(this.fields).forEach((fieldName) => {
      const field = this.fields[fieldName]

      values[fieldName] = field.value
    })

    return values
  }

  unsetValues() {
    this.isChanged = false
    this.isValid = null

    Object.keys(this.fields).forEach((fieldName) => {
      const field = this.fields[fieldName]

      field.unset()
    })
  }

  getErrors(): object {
    const errors = {}

    Object.keys(this.fields).forEach((fieldName) => {
      const field = this.fields[fieldName]

      errors[fieldName] = field.error
    })

    return errors
  }

  async validate(): Promise<boolean> {
    const promises  = Object.keys(this.fields).map((fieldName) => this.fields[fieldName].validate())
    const errors    = await Promise.all(promises)

    this.isValid = errors.every((error) => !error)

    return this.isValid
  }

  async submit(): Promise<any> {
    // validation takes values on start but user may change form values after this moment and before the validation end
    // so if getValues is called after validate() - values may be different in validation and result of sumbit
    // so we should get form values before async validation
    // TODO lock fields on validations start
    const values = this.getValues()

    // TODO don't validate if all fields are changed and valid
    await this.validate()

    if (!this.isValid) {
      const errors = this.getErrors()

      return Promise.reject(errors)
    }

    return values
  }

  on(eventName: string, handler: Function) {
    this._events.subscribe(eventName, handler)
  }

  off(eventName: string, handler: Function) {
    this._events.unsubscribe(eventName, handler)
  }
}


export default Form
